import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";

const NepalVisaInformation = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Visa on Arrival",
      icon: "Stamp",
      content: "Citizens of most countries can obtain a visa on arrival at Tribhuvan International Airport in Kathmandu and at land border crossings. The process is straightforward: fill out the arrival form, pay the fee, and receive your visa.",
      details: [
        "Valid for 15, 30, or 90 days from date of issue",
        "Multiple entry allowed for tourists",
        "Can be extended at Department of Immigration in Kathmandu",
        "Payable in USD, EUR, GBP, or other major currencies",
        "Passport must be valid for at least 6 months"
      ]
    },
    {
      title: "Visa Fees",
      icon: "DollarSign",
      content: "Visa fees are payable in cash (USD preferred) or by credit card at the airport.",
      details: [
        "15 days: USD 30",
        "30 days: USD 50",
        "90 days: USD 125",
        "Gratis visa (free) for SAARC country nationals",
        "Visa extension: USD 3 per day (up to 15 days)"
      ]
    },
    {
      title: "Online Visa Application",
      icon: "Globe",
      content: "You can also apply for a Nepal visa online before your arrival through the official Nepal Immigration website. This can save time at the airport.",
      details: [
        "Apply at https://nepaliport.immigration.gov.np",
        "Fill the form and upload passport photo",
        "Pay online via credit card",
        "Receive confirmation via email",
        "Present confirmation at visa counter on arrival"
      ]
    },
    {
      title: "Required Documents",
      icon: "FileText",
      content: "Make sure you have the following documents ready when applying for your Nepal visa:",
      details: [
        "Valid passport (6+ months validity)",
        "2 passport-size photos",
        "Completed visa application form",
        "Valid return or onward ticket",
        "Hotel booking confirmation"
      ]
    },
    {
      title: "Visa Extension",
      icon: "Clock",
      content: "If you wish to stay longer than your visa allows, you can extend your visa at the Department of Immigration in Kathmandu.",
      details: [
        "Visit Department of Immigration, Kalikasthan, Kathmandu",
        "Fill extension application form",
        "Pay extension fee (USD 3 per day for first 15 days)",
        "Processing takes 1 working day",
        "Maximum extension: 150 days total stay per year"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&h=600&fit=crop)" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="section-heading text-white mb-4">Nepal Visa Information</h1>
              <p className="text-lg text-white/80">Everything you need to know about getting a visa for Nepal.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-12 flex items-start gap-4">
              <Icon name="Info" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Important Note</h3>
                <p className="text-sm text-amber-700">Visa policies may change. Always check the official Nepal Immigration website or consult your nearest Nepali embassy for the most up-to-date information before traveling.</p>
              </div>
            </div>

            <div className="space-y-10">
              {sections.map((section, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Icon name={section.icon} size={18} className="text-accent" />
                    </div>
                    <h2 className="font-headline text-xl text-gray-900" style={{ fontWeight: 300 }}>{section.title}</h2>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-4">{section.content}</p>
                  <ul className="space-y-2">
                    {section.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                        <Icon name="Check" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-white/50">
          &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default NepalVisaInformation;
